import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { ShieldCheck } from 'lucide-react'
import clsx from 'clsx'
import { Button } from '@/components/common/Button'
import { DevActorSwitch } from '@/components/patching/DevActorSwitch'
import { TagDialog } from '@/components/patching/TagDialog'
import { TagManager } from '@/components/patching/TagManager'
import { useConnectionPermissions } from '@/hooks/useConnectionPermissions'
import { patchingKeys } from '@/hooks/usePatching'
import { patchingApi, toPatchApiError } from '@/services/patching'
import { CONNECTION_DELETE_DENIED, CONNECTION_SAVE_DENIED } from '@/utils/connectionPermissions'
import type { AzureSubscriptionConfig, PatchInventoryResource, TagWriteResult, VmTag } from '@/types/patching'
import form from '@/components/patching/Patching.module.css'
import styles from './PatchManagementPage.module.css'

const EMPTY = {
  tenant_id: '',
  subscription_id: '',
  display_name: '',
  auth_method: 'managed_identity',
  client_id: '',
  managed_identity_name: '',
  credential_ref: '',
  cloud: 'AzureCloud',
  resource_group_filter: '',
  region_filter: '',
}

/** How often the page asks Azure whether a started assessment has finished. Azure takes minutes, not seconds. */
const ASSESSMENT_POLL_MS = 15_000

/** The tag filter's "no tag at all" choice. Tag names cannot contain a space, so it can never be a real tag. */
const NO_TAG = 'not in any tag'

const tagsOf = (row: PatchInventoryResource): string[] => row.tags ?? []

/** Which saved connection this browser used last, so a refresh reopens it. A per-viewer convenience only. */
const LAST_CONNECTION_KEY = 'ssp-patch-azure-connection'

function lastConnectionId(): number | null {
  try {
    const value = Number(localStorage.getItem(LAST_CONNECTION_KEY))
    return Number.isInteger(value) && value > 0 ? value : null
  } catch {
    return null
  }
}

function rememberConnectionId(id: number | null) {
  try {
    if (id === null) localStorage.removeItem(LAST_CONNECTION_KEY)
    else localStorage.setItem(LAST_CONNECTION_KEY, String(id))
  } catch {
    // Storage can be unavailable (private window, blocked site data). The connection list still works without it.
  }
}

/** The read-only note's id, so the locked fields and buttons can point to it for screen readers. */
const READ_ONLY_NOTE_ID = 'connection-read-only-note'

/** What the last "Validate connection" click in this session found. It is never read from the saved record. */
interface ValidationResult {
  status: string
  validatedAt: string | null
  checks: NonNullable<AzureSubscriptionConfig['validation_detail']['checks']>
}

/** The form fields a saved connection fills in. Secrets are never stored, so there is nothing secret to fill. */
function draftOf(row: AzureSubscriptionConfig): typeof EMPTY {
  return {
    tenant_id: row.tenant_id,
    subscription_id: row.subscription_id,
    display_name: row.display_name,
    auth_method: row.auth_method,
    client_id: row.client_id,
    managed_identity_name: row.managed_identity_name,
    credential_ref: row.credential_ref,
    cloud: row.cloud,
    resource_group_filter: row.resource_group_filter,
    region_filter: row.region_filter,
  }
}

function connectionLabel(row: AzureSubscriptionConfig): string {
  const state = row.validation_status === 'passed' ? 'connected' : row.validation_status === 'failed' ? 'validation failed' : 'not validated yet'
  return `${row.display_name || row.subscription_id} · ${state}`
}

/** Every machine already stored for a subscription, following the list's paging. */
async function storedResources(row: AzureSubscriptionConfig): Promise<PatchInventoryResource[]> {
  const all: PatchInventoryResource[] = []
  for (let page = 1; page <= 20; page += 1) {
    const batch = await patchingApi.listResources(`?subscription=${encodeURIComponent(row.subscription_id)}&page_size=200&page=${page}`)
    all.push(...batch.results)
    if (batch.results.length === 0 || all.length >= batch.count) break
  }
  return all
}

/** Configure a subscription, discover VMs and Arc servers, and start a DEV campaign from the selection. */
export function AzureInventoryPage() {
  const navigate = useNavigate()
  const [draft, setDraft] = useState(EMPTY)
  const [saved, setSaved] = useState<AzureSubscriptionConfig | null>(null)
  const [resources, setResources] = useState<PatchInventoryResource[]>([])
  const [selected, setSelected] = useState<number[]>([])
  const [openId, setOpenId] = useState<number | null>(null)
  const [kbInclude, setKbInclude] = useState<string[]>([])
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState('')
  const [connections, setConnections] = useState<AzureSubscriptionConfig[]>([])
  const [loadingSaved, setLoadingSaved] = useState(true)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  // VM tags of the open connection, the table's tag filter, and the two tag panels.
  const [tags, setTags] = useState<VmTag[]>([])
  const [tagFilter, setTagFilter] = useState('')
  // What the user typed in the search box: a full or partial machine name.
  const [search, setSearch] = useState('')
  const [tagDialogOpen, setTagDialogOpen] = useState(false)
  const [manageOpen, setManageOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  // The result of "Validate connection" in this session. Empty on load and whenever it stops applying (see clearValidation).
  const [validation, setValidation] = useState<ValidationResult | null>(null)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [validating, setValidating] = useState(false)
  const validatingRef = useRef(false) // ignores a second click before the button has re-rendered as disabled
  const validationRun = useRef(0) // a response that arrives after the result was cleared is dropped
  const queryClient = useQueryClient()
  // Who may change or delete the open connection. The server decides too; this keeps the page honest.
  const permissions = useConnectionPermissions(saved !== null)

  const eligible = resources.filter((row) => row.eligible)
  const excluded = resources.filter((row) => !row.eligible)
  const tagMatches =
    tagFilter === ''
      ? resources
      : tagFilter === NO_TAG
        ? resources.filter((row) => tagsOf(row).length === 0)
        : resources.filter((row) => tagsOf(row).includes(tagFilter))
  // A search matches part of the machine's name, whatever the case. It narrows the tag filter, it does not replace it.
  const needle = search.trim().toLowerCase()
  const visible = needle === '' ? tagMatches : tagMatches.filter((row) => row.name.toLowerCase().includes(needle))
  const filtering = tagFilter !== '' || needle !== ''
  const selectedRows = useMemo(
    () => resources.filter((row) => selected.includes(row.id)),
    [resources, selected],
  )
  // Machines Azure is still assessing. A string key keeps the polling effect from restarting on unrelated updates.
  const assessingKey = resources.filter((row) => row.assessment.state === 'running').map((row) => row.id).join(',')
  const [pollRound, setPollRound] = useState(0)

  useEffect(() => {
    if (assessingKey === '') return
    const ids = assessingKey.split(',').map(Number)
    let cancelled = false
    const timer = window.setTimeout(async () => {
      try {
        const rows = await patchingApi.refreshAssessments(ids)
        if (cancelled) return
        const byId = new Map(rows.map((row) => [row.id, row]))
        setResources((prev) => prev.map((row) => byId.get(row.id) ?? row))
        if (!rows.some((row) => row.assessment.state === 'running')) {
          setMessage('Azure finished assessing. The results are in the table.')
        }
        setPollRound((round) => round + 1) // check again if any machine is still running
      } catch (err) {
        if (!cancelled) setError(toPatchApiError(err).message)
      }
    }, ASSESSMENT_POLL_MS)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [assessingKey, pollRound])

  // The tags belong to the connection, so they are loaded when a connection is opened.
  const savedId = saved?.id ?? null
  useEffect(() => {
    if (savedId === null) return
    let cancelled = false
    patchingApi
      .listTags(savedId)
      .then((rows) => {
        if (!cancelled) setTags(rows)
      })
      .catch((err) => {
        if (!cancelled) setError(toPatchApiError(err).message)
      })
    return () => {
      cancelled = true
    }
  }, [savedId])

  /** Hide the validation result: it no longer describes what is on screen. */
  const clearValidation = useCallback(() => {
    validationRun.current += 1
    setValidation(null)
    setValidationError(null)
  }, [])

  /** Forget the previous connection's tags, search, filter and panels, and its validation result. */
  const resetFilters = useCallback(() => {
    clearValidation()
    setTags([])
    setTagFilter('')
    setSearch('')
    setTagDialogOpen(false)
    setManageOpen(false)
  }, [clearValidation])

  /** Reload the tags and the machines (their Tags column) after a tag changed. */
  async function reloadTags() {
    if (!saved) return
    const [fresh, rows] = await Promise.all([patchingApi.listTags(saved.id), storedResources(saved)])
    setTags(fresh)
    setResources(rows)
    setSelected((prev) => prev.filter((id) => rows.some((row) => row.id === id && row.eligible)))
    setTagFilter((prev) => (prev !== '' && prev !== NO_TAG && !fresh.some((tag) => tag.name === prev) ? '' : prev))
  }

  function update(patch: Partial<typeof EMPTY>) {
    if (permissions.readOnly) return
    clearValidation()
    setDraft((prev) => ({ ...prev, ...patch }))
  }

  /** Keep the saved-connection list in step with a row the API just returned. */
  function upsertConnection(row: AzureSubscriptionConfig) {
    setConnections((prev) => (prev.some((item) => item.id === row.id) ? prev.map((item) => (item.id === row.id ? row : item)) : [...prev, row]))
  }

  /** Make a saved connection the current one: fill the form and show the machines already stored for it. */
  const openConnection = useCallback(async (row: AzureSubscriptionConfig): Promise<number> => {
    const rows = await storedResources(row)
    setSaved(row)
    setDraft(draftOf(row))
    setResources(rows)
    setSelected([])
    setOpenId(null)
    setNote('')
    setConfirmingDelete(false)
    resetFilters()
    rememberConnectionId(row.id)
    return rows.length
  }, [resetFilters])

  // Load the saved connections once. Reopen the one used last (or the only one) so a refresh does not lose it.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const rows = await patchingApi.listSubscriptions()
        if (cancelled) return
        setConnections(rows)
        const pick = rows.find((row) => row.id === lastConnectionId()) ?? (rows.length === 1 ? rows[0] : undefined)
        if (pick) {
          const count = await openConnection(pick)
          if (!cancelled) {
            setMessage(
              `Reopened ${pick.display_name || pick.subscription_id} with ${count} saved machine${count === 1 ? '' : 's'}. ` +
                'Discover resources refreshes the list from Azure.',
            )
          }
        }
      } catch (err) {
        if (!cancelled) setError(toPatchApiError(err).message)
      } finally {
        if (!cancelled) setLoadingSaved(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [openConnection])

  /** Clear the form and the table so a new connection can be entered. */
  function startNewConnection() {
    setSaved(null)
    setDraft(EMPTY)
    setResources([])
    setSelected([])
    setOpenId(null)
    setNote('')
    setError(null)
    setMessage(null)
    setConfirmingDelete(false)
    resetFilters()
    rememberConnectionId(null)
  }

  async function onChooseConnection(value: string) {
    if (value === '') {
      startNewConnection()
      return
    }
    const row = connections.find((item) => item.id === Number(value))
    if (!row) return
    await run(async () => {
      const count = await openConnection(row)
      setMessage(
        `Opened ${row.display_name || row.subscription_id} with ${count} saved machine${count === 1 ? '' : 's'}. ` +
          (row.validation_status === 'passed' ? 'Discover resources refreshes the list from Azure.' : 'Validate the connection before discovering.'),
      )
    })
  }

  /** Run an action with the page busy. `whenForbidden` words the message for a 403, which the page's roles may be out of date for. */
  async function run(action: () => Promise<void>, whenForbidden?: (serverMessage: string) => string) {
    setBusy(true)
    setError(null)
    setMessage(null)
    try {
      await action()
    } catch (err) {
      const failure = toPatchApiError(err)
      if (failure.status === 403) {
        void queryClient.invalidateQueries({ queryKey: patchingKeys.me() })
        setError(whenForbidden ? whenForbidden(failure.message) : failure.message)
      } else {
        setError(failure.message)
      }
    } finally {
      setBusy(false)
    }
  }

  function remember(rows: PatchInventoryResource[]) {
    setResources(rows)
    setSelected((prev) => prev.filter((id) => rows.some((row) => row.id === id && row.eligible)))
  }

  async function onDelete() {
    if (!saved || !permissions.canDelete) return
    const gone = saved
    await run(async () => {
      const result = await patchingApi.deleteSubscription(gone.id)
      setConnections((prev) => prev.filter((item) => item.id !== gone.id))
      startNewConnection()
      setMessage(
        `Deleted ${gone.display_name || gone.subscription_id} and its ${result.resources_removed} saved machine${result.resources_removed === 1 ? '' : 's'}. ` +
          'Nothing in Azure was changed.',
      )
    }, () => CONNECTION_DELETE_DENIED)
    setConfirmingDelete(false)
  }

  async function onSave() {
    if (!permissions.canSave) return
    setSaving(true)
    await run(async () => {
      // Saving the connection that is already open keeps the tag filters stored for it; this form has no tag inputs.
      const sameSubscription = saved?.subscription_id === draft.subscription_id.trim().toLowerCase()
      const row = await patchingApi.saveSubscription({
        ...draft,
        tag_filters: sameSubscription ? saved.tag_filters : {},
      })
      if (!saved || saved.id !== row.id) {
        // A different subscription: the machines on screen belong to the previous one.
        setResources([])
        setSelected([])
        setOpenId(null)
        resetFilters()
      }
      setSaved(row)
      upsertConnection(row)
      rememberConnectionId(row.id)
      clearValidation()
      setMessage('Subscription configuration saved. The client secret is not stored.')
    }, (server) => (server.startsWith('Request failed') ? CONNECTION_SAVE_DENIED : server))
    setSaving(false)
  }

  async function onValidate() {
    if (!saved || validatingRef.current) return
    validatingRef.current = true
    clearValidation()
    const token = validationRun.current
    setValidating(true)
    await run(async () => {
      try {
        const row = await patchingApi.validateSubscription(saved.id)
        setSaved(row)
        upsertConnection(row)
        // Azure fills in the subscription's own name when the display name was left empty.
        setDraft((prev) => (prev.display_name ? prev : { ...prev, display_name: row.display_name }))
        // A field was edited while Azure was answering: the result describes what was saved, not what is on screen now.
        if (token !== validationRun.current) return
        setValidation({ status: row.validation_status, validatedAt: row.last_validated_at, checks: row.validation_detail?.checks ?? [] })
        setMessage(row.validation_status === 'passed' ? 'Connection validated.' : 'Connection validation failed. See the checks below.')
      } catch (err) {
        if (token === validationRun.current) setValidationError(toPatchApiError(err).message)
      } finally {
        validatingRef.current = false
        setValidating(false)
      }
    })
  }

  async function onDiscover() {
    if (!saved) return
    await run(async () => {
      remember(await patchingApi.discoverSubscription(saved.id))
      const fresh = await patchingApi.listSubscriptions()
      setConnections(fresh)
      setSaved(fresh.find((row) => row.id === saved.id) ?? saved)
      setMessage('Discovery finished. Unsupported resource types stay in the list with an exclusion reason.')
    })
  }

  async function onAssess() {
    const ids = selectedRows.map((row) => row.id)
    if (ids.length === 0) return
    await run(async () => {
      const started = await patchingApi.startAssessment(ids)
      const byId = new Map(started.map((row) => [row.id, row]))
      setResources((prev) => prev.map((row) => byId.get(row.id) ?? row))
      const running = started.filter((row) => row.assessment.state === 'running').length
      const refused = started.length - running
      setMessage(
        running === 0
          ? 'Azure did not start any assessment. Open a machine to see why.'
          : `Started an Azure Update Manager assessment on ${running} machine${running === 1 ? '' : 's'}. ` +
              'Azure takes a few minutes. This page checks every 15 seconds and fills in the results.' +
              (refused > 0 ? ` ${refused} could not be started: open the machine to see why.` : ''),
      )
    })
  }

  /** Read the latest assessment Azure holds, including ones started in the Azure portal or by periodic assessment. */
  async function onRefresh() {
    const ids = selectedRows.map((row) => row.id)
    if (ids.length === 0) return
    await run(async () => {
      const rows = await patchingApi.refreshAssessments(ids)
      const byId = new Map(rows.map((row) => [row.id, row]))
      setResources((prev) => prev.map((row) => byId.get(row.id) ?? row))
      setPollRound((round) => round + 1)
      const running = rows.filter((row) => row.assessment.state === 'running').length
      const loaded = rows.filter((row) => row.assessment.state !== 'running' && row.last_assessed).length
      setMessage(
        `Loaded the latest Azure assessment for ${loaded} of ${rows.length} machine${rows.length === 1 ? '' : 's'}.` +
          (running > 0 ? ` ${running} still assessing.` : '') +
          ' Machines Azure has no assessment for stay unverified.',
      )
    })
  }

  async function onRecommend() {
    const ids = selectedRows.map((row) => row.id)
    if (ids.length === 0) return
    await run(async () => {
      const advice = await patchingApi.recommendResources(ids)
      setNote(advice.note)
      const listed = await patchingApi.listResources('?page_size=200')
      setResources(listed.results)
      setMessage(`Recommendations use source “${advice.source}”. They do not authorise installation.`)
    })
  }

  function closeTagDialog() {
    setTagDialogOpen(false)
    document.getElementById('add-tags-button')?.focus()
  }

  async function onTagSaved(result: TagWriteResult) {
    closeTagDialog()
    await run(async () => {
      await reloadTags()
      const left = result.excluded ?? []
      const failed = result.azure_tags?.enabled ? (result.azure_tags.failed ?? []) : []
      setMessage(
        `Tag “${result.tag.name}” now has ${result.tag.member_count} VM${result.tag.member_count === 1 ? '' : 's'}. ` +
          'A campaign can name it instead of listing them.' +
          (left.length > 0 ? ` Left out because they are not eligible: ${left.map((item) => item.name).join(', ')}.` : '') +
          (failed.length > 0 ? ` Azure resource tags could not be written for ${failed.length} VM${failed.length === 1 ? '' : 's'}.` : ''),
      )
    })
  }

  function toggle(id: number, on: boolean) {
    setSelected((prev) => (on ? [...new Set([...prev, id])] : prev.filter((item) => item !== id)))
  }

  /**
   * Select every eligible machine on screen and keep what is already selected, so searches can be added up: search "web",
   * select them, search "sql", select those. With no search or tag filter every eligible machine is on screen, so this
   * selects them all, as it always did.
   */
  function selectEligibleShown() {
    setSelected((prev) => [...new Set([...prev, ...visible.filter((row) => row.eligible).map((row) => row.id)])])
  }

  function selectWhere(match: (row: PatchInventoryResource) => boolean) {
    // Only the machines on screen: with a tag filter on, "Select eligible" means the eligible machines of that tag.
    setSelected(visible.filter((row) => row.eligible && match(row)).map((row) => row.id))
  }

  function createCampaign() {
    const chosen = selectedRows.filter((row) => row.eligible)
    const groups = [...new Set(chosen.map((row) => row.resource_group))]
    navigate('/patch-management/new', {
      state: {
        inventoryDraft: {
          name: saved?.display_name ? `${saved.display_name} DEV patch` : 'DEV patch from inventory',
          description: 'DEV campaign created from the discovered Azure inventory.',
          resource_group: groups.length === 1 ? groups[0] : '',
          vm_names: chosen.map((row) => row.name),
          kb_include: kbInclude,
        },
      },
    })
  }

  function toggleKb(kb: string, on: boolean) {
    setKbInclude((prev) => (on ? [...new Set([...prev, kb])] : prev.filter((item) => item !== kb)))
  }

  // A connection the user may not change: text fields stay selectable but cannot be edited, and point to the note.
  const locked = {
    readOnly: permissions.readOnly,
    'aria-readonly': permissions.readOnly ? (true as const) : undefined,
    'aria-describedby': permissions.readOnly ? READ_ONLY_NOTE_ID : undefined,
  }
  const shownIds = new Set(visible.map((row) => row.id))
  const hiddenSelected = selectedRows.filter((row) => !shownIds.has(row.id)).length

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><ShieldCheck size={20} /></div>
          <div>
            <h1 className={styles.pageTitle}>Azure inventory</h1>
            <p className={styles.pageSubtitle}>
              <Link to="/patch-management">Patch campaigns</Link>
              {' · '}Subscription identity, discovery and assessment. Secrets stay in Key Vault.
            </p>
          </div>
        </div>
        <DevActorSwitch />
      </div>

      {error && <div className={clsx(form.alert, form.alertError)} role="alert">{error}</div>}
      {message && <div className={clsx(form.alert, form.alertInfo)} role="status">{message}</div>}

      <section className={form.stack}>
        <label className={form.field}>
          Saved connection
          <select
            className={form.select}
            aria-label="Saved connection"
            value={saved?.id ?? ''}
            onChange={(e) => void onChooseConnection(e.target.value)}
            disabled={busy || loadingSaved}
          >
            <option value="">
              {loadingSaved ? 'Loading saved connections…' : connections.length === 0 ? 'No saved connections yet' : '— Select a saved connection —'}
            </option>
            {connections.map((row) => (
              <option key={row.id} value={row.id}>{connectionLabel(row)}</option>
            ))}
          </select>
        </label>
        <div className={form.actionRow}>
          <Button variant="secondary" onClick={startNewConnection} disabled={busy || loadingSaved}>Add new connection</Button>
          {saved && (
            <Button
              variant="danger"
              onClick={() => setConfirmingDelete(true)}
              disabled={busy || confirmingDelete || !permissions.canDelete}
              title={permissions.deleteBlockedReason ?? undefined}
              aria-describedby={permissions.readOnly ? READ_ONLY_NOTE_ID : undefined}
            >
              Delete connection
            </Button>
          )}
        </div>
        {confirmingDelete && saved && (
          <div className={clsx(form.alert, form.alertError)} role="alertdialog" aria-label="Confirm delete connection">
            <p>
              Delete <strong>{saved.display_name || saved.subscription_id}</strong>? This removes the saved connection and the {resources.length} machine
              {resources.length === 1 ? '' : 's'} and assessment results stored for it. Nothing in Azure changes. Patch campaigns already created are kept, but
              their pre-patch checks look machines up in this inventory, so they will fail until you add the connection again and run Discover resources.
            </p>
            <div className={form.actionRow}>
              <Button variant="danger" onClick={onDelete} loading={busy} disabled={busy}>Yes, delete this connection</Button>
              <Button variant="secondary" onClick={() => setConfirmingDelete(false)} disabled={busy}>Cancel</Button>
            </div>
          </div>
        )}
        <p className={form.muted}>
          {saved
            ? 'Saved on the server with the machines found for it, so it is still here after a refresh. No secret is stored.'
            : 'New connection: fill in the details, click Save configuration, then Validate connection. It is saved on the server (no secret is stored) and appears in the list above.'}
        </p>
        {/* Always in the page so a screen reader announces the note when it appears. */}
        <div aria-live="polite">
          {permissions.readOnlyNote && (
            <div id={READ_ONLY_NOTE_ID} className={clsx(form.alert, form.alertInfo)}>{permissions.readOnlyNote}</div>
          )}
        </div>
        <div className={form.factGrid}>
          <label className={form.field}>Tenant ID<input className={form.input} value={draft.tenant_id} onChange={(e) => update({ tenant_id: e.target.value })} {...locked} /></label>
          <label className={form.field}>Subscription ID<input className={form.input} value={draft.subscription_id} onChange={(e) => update({ subscription_id: e.target.value })} {...locked} /></label>
          <label className={form.field}>Display name<input className={form.input} value={draft.display_name} onChange={(e) => update({ display_name: e.target.value })} {...locked} /></label>
          <label className={form.field}>Authentication
            <select className={form.select} value={draft.auth_method} onChange={(e) => update({ auth_method: e.target.value })} disabled={permissions.readOnly} aria-describedby={locked['aria-describedby']}>
              <option value="managed_identity">Managed identity</option>
              <option value="workload_identity">Workload identity</option>
              <option value="service_principal">Service principal</option>
            </select>
          </label>
          {draft.auth_method === 'managed_identity' ? (
            <>
              <label className={form.field}>Managed identity name<input className={form.input} value={draft.managed_identity_name} onChange={(e) => update({ managed_identity_name: e.target.value })} placeholder="ami-eastus-rnd-plteng" {...locked} /></label>
              <label className={form.field}>Managed identity client ID<input className={form.input} value={draft.client_id} onChange={(e) => update({ client_id: e.target.value })} placeholder="client id of that identity" {...locked} /></label>
            </>
          ) : (
            <>
              <label className={form.field}>Client ID<input className={form.input} value={draft.client_id} onChange={(e) => update({ client_id: e.target.value })} {...locked} /></label>
              <label className={form.field}>Key Vault secret URI<input className={form.input} value={draft.credential_ref} onChange={(e) => update({ credential_ref: e.target.value })} placeholder="https://vault.vault.azure.net/secrets/name" {...locked} /></label>
            </>
          )}
          <label className={form.field}>Cloud
            <select className={form.select} value={draft.cloud} onChange={(e) => update({ cloud: e.target.value })} disabled={permissions.readOnly} aria-describedby={locked['aria-describedby']}>
              <option value="AzureCloud">Azure Public Cloud</option>
              <option value="AzureUSGovernment">Azure US Government</option>
              <option value="AzureChinaCloud">Azure China</option>
            </select>
          </label>
          <label className={form.field}>Resource group filter<input className={form.input} value={draft.resource_group_filter} onChange={(e) => update({ resource_group_filter: e.target.value })} {...locked} /></label>
          <label className={form.field}>Region filter<input className={form.input} value={draft.region_filter} onChange={(e) => update({ region_filter: e.target.value })} {...locked} /></label>
        </div>
        <div className={form.actionRow}>
          <Button
            onClick={onSave}
            loading={saving}
            disabled={busy || !permissions.canSave}
            title={permissions.saveBlockedReason ?? undefined}
            aria-describedby={locked['aria-describedby']}
          >
            Save configuration
          </Button>
          <Button
            variant="secondary"
            onClick={onValidate}
            loading={validating}
            disabled={busy || !saved}
            title={saved ? undefined : 'Save the connection first, then validate it.'}
          >
            Validate connection
          </Button>
          <Button
            variant="secondary"
            onClick={onDiscover}
            disabled={busy || saved?.validation_status !== 'passed'}
            title={saved?.validation_status === 'passed' ? undefined : 'Discovery needs a connection that has passed validation.'}
          >
            Discover resources
          </Button>
        </div>
        {/* What "Validate connection" found, and only that: nothing here is read from the saved record. */}
        <div aria-live="polite" data-testid="validation-result">
          {validating && <p className={form.muted}>Validating the connection…</p>}
          {validationError && <div className={clsx(form.alert, form.alertError)}>Validation could not be run: {validationError}</div>}
          {validation && saved && (
            <>
              <p className={form.muted}>
                Status {validation.status}. Last validated {validation.validatedAt ?? 'never'}. Last discovery {saved.last_discovery_at ?? 'never'}.
                {saved.display_name ? ` Subscription name: ${saved.display_name}.` : ''}
              </p>
              {validation.checks.length > 0 && (
                <ul>
                  {validation.checks.map((check) => (
                    <li key={check.name}>
                      <strong>{check.name}</strong> {check.status}: {check.detail} {check.remediation}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </section>

      <section className={form.stack}>
        <div className={form.actionRow}>
          <span className={form.muted}>
            {selectedRows.length} selected{hiddenSelected > 0 ? ` (${hiddenSelected} not shown)` : ''} · {excluded.length} excluded · {eligible.length} eligible
          </span>
          <Button variant="ghost" onClick={selectEligibleShown} disabled={!visible.some((row) => row.eligible)}>Select eligible</Button>
          <Button variant="ghost" onClick={() => setSelected([])}>Deselect all</Button>
          <Button id="add-tags-button" variant="secondary" onClick={() => setTagDialogOpen(true)} disabled={busy || !saved || selectedRows.length === 0}>
            Add tags
          </Button>
          <Button
            variant="secondary"
            onClick={onAssess}
            disabled={busy || selectedRows.length === 0 || selectedRows.some((row) => row.assessment.state === 'running')}
          >
            Assess patches
          </Button>
          <Button variant="secondary" onClick={onRefresh} disabled={busy || selectedRows.length === 0}>Refresh results</Button>
          <Button variant="secondary" onClick={onRecommend} disabled={busy || selectedRows.length === 0}>Recommend</Button>
          <Button onClick={createCampaign} disabled={selectedRows.length === 0}>Create DEV campaign</Button>
        </div>
        {assessingKey !== '' && (
          <p className={form.muted} role="status">
            Azure is assessing {assessingKey.split(',').length} machine{assessingKey.includes(',') ? 's' : ''}. This page checks every 15 seconds.
          </p>
        )}
        {note && <p className={form.muted}>{note}</p>}
        <div className={form.tagToolbar}>
          <label className={form.label} htmlFor="vm-search">Search</label>
          <input
            id="vm-search"
            type="search"
            className={form.input}
            style={{ width: 280 }}
            placeholder="Type a full or partial machine name"
            autoComplete="off"
            spellCheck={false}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <label className={form.label} htmlFor="tag-filter">Filter by tag</label>
          <select id="tag-filter" className={form.select} style={{ width: 'auto' }} value={tagFilter} onChange={(event) => setTagFilter(event.target.value)}>
            <option value="">All machines</option>
            <option value={NO_TAG}>Not in any tag</option>
            {tags.map((tag) => (
              <option key={tag.id} value={tag.name}>{`${tag.name} (${tag.member_count})`}</option>
            ))}
          </select>
          <Button variant="secondary" size="sm" aria-expanded={manageOpen} onClick={() => setManageOpen((open) => !open)} disabled={!saved}>
            Manage tags
          </Button>
          {filtering && <span className={form.muted} role="status">Showing {visible.length} of {resources.length} machines.</span>}
        </div>
        {manageOpen && saved && <TagManager tags={tags} onChanged={reloadTags} onMessage={setMessage} />}
        <div className={form.tableWrap}>
          <table className={form.table}>
            <thead>
              <tr>
                <th />
                <th>Name</th>
                <th>Group</th>
                <th>Region</th>
                <th>OS</th>
                <th>Environment</th>
                <th>Tags</th>
                <th>Power</th>
                <th>Pending</th>
                <th>Compliance</th>
                <th>Eligible</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <Fragment key={row.id}>
                  <tr>
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.includes(row.id)}
                        disabled={!row.eligible}
                        aria-label={`Select ${row.name}`}
                        onChange={(event) => toggle(row.id, event.target.checked)}
                      />
                    </td>
                    <td>
                      <button type="button" className={form.chip} onClick={() => setOpenId(openId === row.id ? null : row.id)}>{row.name}</button>
                    </td>
                    <td>{row.resource_group}</td>
                    <td>{row.region}</td>
                    <td>{row.os_type}</td>
                    <td>{row.environment}</td>
                    <td>
                      {tagsOf(row).length > 0 && (
                        <span className={form.tagChips} data-testid={`tags-${row.name}`}>
                          {tagsOf(row).map((name) => (
                            <span key={name} className={form.tagChip}>{name}</span>
                          ))}
                        </span>
                      )}
                    </td>
                    <td>{row.power_state}</td>
                    <td>{row.pending_count ?? 'Not available from assessment source'}</td>
                    <td>{row.assessment.state === 'running' ? 'assessing…' : row.compliance_status}</td>
                    <td>{row.eligible ? 'Yes' : row.exclusion_reason}</td>
                  </tr>
                  {openId === row.id && (
                    <tr>
                      <td colSpan={11}>
                        <p className={form.muted}>{row.resource_id}</p>
                        {row.assessment.state === 'running' && <p>{row.assessment.limitation}</p>}
                        {row.assessment.state !== 'running' && row.patches.length === 0 && (
                          <p>No individual packages were returned. {row.assessment.limitation || 'Not available from assessment source.'}</p>
                        )}
                        {row.patches.length > 0 && (
                          <p className={form.muted}>
                            Checked KB numbers are added to the DEV campaign scope. If Azure returned only a classification count, individual packages cannot be selected.
                          </p>
                        )}
                        {row.patches.map((patch) => {
                          const kb = patch.kb_or_package ?? ''
                          return (
                            <label key={`${row.id}-${kb}-${patch.name}`} className={form.checkboxRow}>
                              <input
                                type="checkbox"
                                checked={kb !== '' && kbInclude.includes(kb)}
                                disabled={kb === ''}
                                onChange={(event) => toggleKb(kb, event.target.checked)}
                              />
                              <span>
                                {patch.name} {kb ? `(${kb})` : '(Not available from assessment source)'} · {patch.classification || 'Unknown'} · reboot {patch.reboot || 'Unknown'}
                              </span>
                            </label>
                          )
                        })}
                        {row.recommendations.map((item) => (
                          <p key={item.id}>
                            {item.priority} {item.recommendation}: {item.update_name}. {item.rationale} Source {item.source}. {item.confidence_meaning}
                          </p>
                        ))}
                        <div className={form.actionRow}>
                          <Button variant="ghost" onClick={() => selectWhere((item) => item.resource_group === row.resource_group)}>Select group</Button>
                          <Button variant="ghost" onClick={() => selectWhere((item) => item.environment === row.environment)}>Select environment</Button>
                          <Button variant="ghost" onClick={() => selectWhere((item) => item.application === row.application)}>Select application</Button>
                          <Button variant="ghost" onClick={() => selectWhere((item) => item.compliance_status === row.compliance_status)}>Select compliance</Button>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {resources.length === 0 && <p className={form.muted}>No resources yet. Validate the connection, then discover.</p>}
        {resources.length > 0 && visible.length === 0 && <p className={form.muted}>No machine matches the search or tag filter.</p>}
      </section>
      {tagDialogOpen && saved && (
        <TagDialog connectionId={saved.id} rows={selectedRows} tags={tags} onClose={closeTagDialog} onSaved={(result) => void onTagSaved(result)} />
      )}
    </div>
  )
}
